import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Users, TrendingUp, UserCheck, Building2 } from 'lucide-react';
import ScrollReveal from '../effects/ScrollReveal';

const statsData = [
  { value: 5000, suffix: '+', label: 'Students Enrolled', icon: Users, desc: 'Across all programs' },
  { value: 94, suffix: '%', label: 'Placement Rate', icon: TrendingUp, desc: 'Within 6 months' },
  { value: 25, suffix: '+', label: 'Expert Instructors', icon: UserCheck, desc: 'Industry professionals' },
  { value: 10, suffix: '+', label: 'Industry Partners', icon: Building2, desc: 'Hiring our graduates' },
];

function AnimatedCounter({ target, suffix }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / (1800 / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

export default function StatsSection() {
  return (
    <section className="py-16 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statsData.map((stat, i) => (
            <ScrollReveal key={stat.label} variant="fadeUp" delay={i * 0.1}>
              <motion.div
                whileHover={{ y: -8, scale: 1.03 }}
                transition={{ type: 'spring', stiffness: 280, damping: 18 }}
                className="glass rounded-2xl border border-white/80 p-6 text-center relative overflow-hidden group cursor-default shadow-sm"
              >
                {/* Hover glow */}
                <motion.div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(254,210,2,0.12) 0%, transparent 70%)' }} />
                
                {/* Icon */}
                <motion.div
                  whileHover={{ scale: 1.2, rotate: 12 }}
                  transition={{ type: 'spring', stiffness: 350 }}
                  className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/25"
                >
                  <stat.icon className="w-6 h-6 text-primary-foreground" />
                </motion.div>

                {/* Number */}
                <p className="font-heading text-3xl sm:text-4xl font-bold text-primary tracking-tight">
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </p>
                <p className="text-sm font-semibold text-foreground mt-1 font-body">{stat.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5 font-body">{stat.desc}</p>

                {/* Bottom accent */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.1, duration: 0.6 }}
                  className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary/30 rounded-full origin-left"
                />
              </motion.div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}